import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { jsPDF } from "jspdf";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { toast } from "sonner";
import { Search, ScrollText, Users, ChevronDown, ChevronRight, Sparkles, Loader2, ShieldAlert, Upload, Database, FileDown, Globe, ExternalLink, BookOpen, FileText, Image, Save, Download, Trash2, FolderOpen, Check } from "lucide-react";

// Minimal markdown renderer supporting headings, bold, bullets and [text](url) links.
function renderReport(md) {
  if (!md) return null;
  const inline = (t, base) => {
    const nodes = [];
    let rest = t, key = 0;
    const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\)/;
    let m;
    while ((m = rest.match(re))) {
      if (m.index > 0) nodes.push(<span key={`${base}-${key++}`}>{rest.slice(0, m.index)}</span>);
      if (m[1]) nodes.push(<strong key={`${base}-${key++}`} className="text-[#180D24]">{m[1]}</strong>);
      else nodes.push(<a key={`${base}-${key++}`} href={m[3]} target="_blank" rel="noreferrer" className="text-[#59288E] underline font-medium hover:text-[#180D24]">{m[2]}</a>);
      rest = rest.slice(m.index + m[0].length);
    }
    if (rest) nodes.push(<span key={`${base}-${key++}`}>{rest}</span>);
    return nodes;
  };
  const out = []; let list = [];
  const flush = (i) => { if (list.length) { out.push(<ul key={`ul-${i}`} className="list-disc pl-6 space-y-1 my-2 text-sm text-[#1A171D]">{list}</ul>); list = []; } };
  md.split("\n").forEach((raw, i) => {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) { flush(i); out.push(<h4 key={i} className="font-serif text-base font-semibold text-[#59288E] mt-4 mb-1.5">{line.slice(3)}</h4>); }
    else if (line.startsWith("# ")) { flush(i); out.push(<h3 key={i} className="font-serif text-lg font-bold text-[#180D24] mt-2 mb-2">{line.slice(2)}</h3>); }
    else if (/^\d+\.\s/.test(line)) { list.push(<li key={i} className="list-decimal">{inline(line.replace(/^\d+\.\s/, ""), i)}</li>); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { list.push(<li key={i}>{inline(line.slice(2), i)}</li>); }
    else if (line === "" || line === "---") { flush(i); }
    else { flush(i); out.push(<p key={i} className="text-sm text-[#1A171D] leading-relaxed my-1.5">{inline(line, i)}</p>); }
  });
  flush("end");
  return out;
}

function exportResearchPdf({ title, report, sources }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const M = 48, W = 612 - M * 2; let y = 58;
  doc.setFont("times", "bold"); doc.setFontSize(16); doc.setTextColor(24, 13, 36);
  doc.text(doc.splitTextToSize(title || "AI Deep Research", W), M, y); y += 22;
  doc.setFont("times", "italic"); doc.setFontSize(9); doc.setTextColor(120);
  doc.text(doc.splitTextToSize("WhatsMyTribe.com — AI Deep Research. Research leads from public sources only, NOT proof of identity or tribal citizenship. Verify every source independently.", W), M, y); y += 24;
  doc.setTextColor(20);
  (report || "").split("\n").forEach((raw) => {
    let line = raw.replace(/\*\*/g, "").replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, "$1");
    let size = 10, bold = false;
    if (line.startsWith("## ")) { line = line.slice(3); size = 12; bold = true; y += 6; }
    else if (line.startsWith("# ")) { line = line.slice(2); size = 13; bold = true; }
    else if (line.startsWith("- ") || line.startsWith("* ")) { line = "•  " + line.slice(2); }
    else if (/^\d+\.\s/.test(line)) { /* keep numbering */ }
    doc.setFont("times", bold ? "bold" : "normal"); doc.setFontSize(size);
    doc.splitTextToSize(line, W).forEach((w) => { if (y > 740) { doc.addPage(); y = 58; } doc.text(w, M, y); y += size + 4; });
  });
  if (sources?.length) {
    y += 10; if (y > 710) { doc.addPage(); y = 58; }
    doc.setFont("times", "bold"); doc.setFontSize(12); doc.setTextColor(24, 13, 36); doc.text("Live web sources", M, y); y += 16;
    doc.setFontSize(9);
    sources.forEach((s, i) => {
      if (y > 720) { doc.addPage(); y = 58; }
      doc.setFont("times", "bold"); doc.setTextColor(20);
      doc.splitTextToSize(`${i + 1}. ${s.title || s.url}`, W).forEach((w) => { doc.text(w, M, y); y += 13; });
      doc.setFont("times", "normal"); doc.setTextColor(30, 60, 170);
      doc.splitTextToSize(s.url, W - 10).forEach((w) => { doc.textWithLink(w, M + 10, y, { url: s.url }); y += 12; });
      doc.setTextColor(20); y += 5;
    });
  }
  doc.save(`${(title || "deep-research").replace(/[^a-z0-9]+/gi, "_").slice(0, 50)}.pdf`);
}

function DeepResearchPanel({ report, sources, dbMatchCount, disclaimer, title, subject, onSave, saved }) {
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(!!saved);
  const doSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try { await onSave({ title, report, sources, subject: subject || {}, db_match_count: dbMatchCount }); setIsSaved(true); }
    finally { setSaving(false); }
  };
  return (
    <Panel className="p-5 mb-4 border-[#59288E]/25 bg-[#FCFAFF]" data-testid="deep-research-panel">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 text-[#180D24] font-serif text-lg font-semibold">
          <Globe size={18} className="text-[#59288E]" /> {title || "AI Deep Research"}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-research-btn" onClick={() => exportResearchPdf({ title, report, sources })}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#59288E]/40 text-[#59288E] px-3 py-1.5 text-xs font-semibold hover:bg-[#59288E] hover:text-white transition-colors">
            <Download size={13} /> Export PDF
          </button>
          {onSave ? (
            <button data-testid="save-research-btn" onClick={doSave} disabled={saving || isSaved}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] text-[#180D24] px-3 py-1.5 text-xs font-bold hover:bg-[#E7C965] transition-colors disabled:opacity-70">
              {isSaved ? <Check size={13} /> : saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {isSaved ? "Saved" : "Save to vault"}
            </button>
          ) : null}
        </div>
      </div>
      {disclaimer ? <div className="rounded-lg bg-[#FFFBEB] border border-[#F59E0B]/30 p-2.5 text-xs text-[#B45309] mb-3 flex items-start gap-2"><ShieldAlert size={14} className="shrink-0 mt-0.5" />{disclaimer}</div> : null}
      <div className="prose-sm" data-testid="deep-research-report">{renderReport(report)}</div>
      {sources?.length ? (
        <div className="mt-4" data-testid="deep-research-sources">
          <div className="text-xs font-bold uppercase tracking-wide text-[#8F7220] mb-2">Live web sources ({sources.length})</div>
          <div className="space-y-2">
            {sources.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`source-link-${i}`}
                className="block rounded-lg border border-[#E4DCD3] bg-white p-2.5 hover:border-[#59288E] transition-colors">
                <div className="text-sm font-semibold text-[#59288E] flex items-center gap-1.5"><ExternalLink size={13} /> {s.title || s.url}</div>
                <div className="text-[11px] text-[#8F7220] font-mono truncate">{s.url}</div>
                {s.snippet ? <div className="text-xs text-[#756D7E] mt-1 line-clamp-2">{s.snippet}</div> : null}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </Panel>
  );
}


const STATUS_STYLE = {
  ENROLLED: "bg-[#DCFCE7] text-[#166534]", APPROVED: "bg-[#DCFCE7] text-[#166534]",
  REJECTED: "bg-[#FEE2E2] text-[#B91C1C]", APPLICATION: "bg-[#FEF3C7] text-[#B45309]",
  CENSUS: "bg-[#EDE9FE] text-[#5B21B6]", PAYMENT: "bg-[#E0F2FE] text-[#075985]",
  REMOVAL: "bg-[#FFE4E6] text-[#9F1239]", FREEDMEN: "bg-[#F3E8FF] text-[#6B21A8]",
};
const STRENGTH_STYLE = { Strong: "bg-[#10B981] text-white", Possible: "bg-[#D4AF37] text-[#180D24]", Weak: "bg-[#E4DCD3] text-[#756D7E]" };

const BLANK = { first: "", last: "", birth_year: "", state: "", tribe: "", roll: "", source_key: "", spelling_variants: true, phonetic: true, search_relatives: true };

function RecordCard({ rec }) {
  const st = STATUS_STYLE[rec.status] || "bg-[#E4DCD3] text-[#756D7E]";
  return (
    <div className="rounded-xl border border-[#E4DCD3] bg-white p-4" data-testid={`roll-result-${rec.record_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-serif text-lg font-semibold text-[#180D24]">{rec.name_as_written}</div>
          <div className="text-xs text-[#8F7220] font-mono">{rec.roll_name} · {rec.record_year} · {rec.tribe}</div>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${st}`}>{rec.status}</span>
      </div>
      <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#1A171D]">
        {rec.birth_year ? <div><span className="text-[#8F7220] text-xs">Born ~</span> {rec.birth_year}</div> : null}
        {(rec.county || rec.state_or_territory) ? <div><span className="text-[#8F7220] text-xs">Place</span> {[rec.county, rec.state_or_territory].filter(Boolean).join(", ")}</div> : null}
        {rec.roll_number ? <div><span className="text-[#8F7220] text-xs">Roll #</span> {rec.roll_number}</div> : null}
        {rec.application_number ? <div><span className="text-[#8F7220] text-xs">App #</span> {rec.application_number}</div> : null}
      </div>
      {rec.family_members?.length ? <div className="mt-1 text-xs text-[#756D7E]">Household: {rec.family_members.join(", ")}</div> : null}
      {rec.transcription ? <div className="mt-2 text-xs italic text-[#59288E] bg-[#FAF8F5] rounded-lg p-2">“{rec.transcription}”</div> : null}
      <div className="mt-1 text-[10px] font-mono text-[#B49A6A]">{rec.source_citation} · {rec.source_archive}</div>
      {rec.notes ? <div className="mt-1 text-[11px] text-[#B45309]">{rec.notes}</div> : null}
    </div>
  );
}

export default function RollSearch() {
  const [catalog, setCatalog] = useState(null);
  const [f, setF] = useState(BLANK);
  const [results, setResults] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");
  const [busy, setBusy] = useState(false);
  const [famBusy, setFamBusy] = useState(false);
  const [famMatches, setFamMatches] = useState(null);
  const [deep, setDeep] = useState(null);
  const [deepBusy, setDeepBusy] = useState(false);
  const [famDeep, setFamDeep] = useState(null);
  const [famDeepBusy, setFamDeepBusy] = useState(false);
  const [openEra, setOpenEra] = useState(null);
  const [importing, setImporting] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [ocrJobs, setOcrJobs] = useState([]);
  const importRef = React.useRef(null);

  const reloadCatalog = () => api.get("/rolls/catalog").then((r) => setCatalog(r.data));
  const loadSaved = () => api.get("/rolls/research/saved").then((r) => setSavedList(r.data.items)).catch(() => {});
  const loadOcr = () => api.get("/rolls/ocr-status").then((r) => setOcrJobs(r.data.jobs || [])).catch(() => {});
  useEffect(() => { reloadCatalog(); loadSaved(); loadOcr(); }, []);
  if (!catalog) return <Loader />;

  const saveResearch = async (payload) => {
    const { data } = await api.post("/rolls/research/save", payload);
    setSavedList((s) => [data, ...s]);
    toast.success("Saved to your research vault");
  };
  const deleteSaved = async (id) => {
    await api.delete(`/rolls/research/saved/${id}`);
    setSavedList((s) => s.filter((x) => x.id !== id));
    toast.success("Removed from vault");
  };
  const reopenSaved = (it) => {
    setResults(null); setFamMatches(null); setFamDeep(null);
    setDeep({ report: it.report, sources: it.sources, db_match_count: it.db_match_count,
              disclaimer: "Saved research — reopened from your vault (no AI was re-run)." });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (k) => () => setF((s) => ({ ...s, [k]: !s[k] }));
  const inputCls = "w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2.5 text-sm focus:border-[#59288E] outline-none";
  const allRolls = catalog.catalog.flatMap((e) => e.rolls.map((r) => r.name));
  const ocrMap = Object.fromEntries(ocrJobs.map((j) => [j.source_key, j]));

  const runSearch = async () => {
    setBusy(true); setFamMatches(null); setFamDeep(null); setDeep(null);
    // Run live AI Deep Research concurrently (needs a name).
    if (f.first || f.last) {
      setDeepBusy(true);
      api.post("/rolls/deep-research", f)
        .then(({ data }) => setDeep(data))
        .catch((e) => toast.error(e?.response?.data?.detail || "Deep research failed"))
        .finally(() => setDeepBusy(false));
    }
    try {
      const { data } = await api.post("/rolls/search", f);
      setResults(data.results); setDisclaimer(data.disclaimer);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    finally { setBusy(false); }
  };
  const searchFamily = async () => {
    setFamBusy(true); setResults(null); setDeep(null); setFamDeep(null);
    setFamDeepBusy(true);
    // Kick off the long-running family Deep Research as a background job, then poll for progress.
    (async () => {
      try {
        const { data } = await api.post("/rolls/deep-research-family");
        const jobId = data.job_id;
        const poll = async () => {
          try {
            const { data: s } = await api.get(`/rolls/deep-research-family/${jobId}`);
            setFamDeep({ count: s.count, reports: s.reports || [], disclaimer: s.disclaimer, total: s.total, done: s.done });
            if (s.status === "running") { setTimeout(poll, 3000); return; }
            setFamDeepBusy(false);
            if (s.status === "error") toast.error("Family deep research failed");
          } catch (e) { setFamDeepBusy(false); toast.error("Family deep research failed"); }
        };
        poll();
      } catch (e) { setFamDeepBusy(false); toast.error(e?.response?.data?.detail || "Family deep research failed"); }
    })();
    try {
      const { data } = await api.post("/rolls/search-my-family");
      setFamMatches(data.matches);
      if (!data.matches.length) toast.info("No potential matches found across the indexed rolls yet.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Family scan failed"); }
    finally { setFamBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/rolls/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "roll_import_template.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Could not download template"); }
  };
  const importCsv = async (fileObj) => {
    if (!fileObj) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", fileObj);
      const { data } = await api.post("/rolls/import", fd);
      toast.success(`Imported ${data.imported} records${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      await reloadCatalog();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <div data-testid="module-roll-search">
      <PageHeader
        eyebrow="Research • Records"
        title="Indian Roll Search"
        subtitle="Search historic Indian rolls, censuses, muster and payment records by name, family member, place, age, roll number and tribe — with spelling variants, phonetic and wildcard matching (John*, ?mith)."
        right={
          <button data-testid="search-my-family-btn" onClick={searchFamily} disabled={famBusy}
            className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60">
            {famBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} className="text-[#E7C965]" />} Search My Entire Family
          </button>
        }
      />

      <Panel className="p-5 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input data-testid="roll-first-input" value={f.first} onChange={set("first")} className={inputCls} placeholder="First name (wildcards ok: John*)" />
          <input data-testid="roll-last-input" value={f.last} onChange={set("last")} className={inputCls} placeholder="Last name (e.g. ?mith)" />
          <input data-testid="roll-birth-input" value={f.birth_year} onChange={set("birth_year")} className={inputCls} placeholder="Approx. birth year" />
          <input data-testid="roll-state-input" value={f.state} onChange={set("state")} className={inputCls} placeholder="State / Territory" />
          <input data-testid="roll-tribe-input" value={f.tribe} onChange={set("tribe")} className={inputCls} placeholder="Tribe / Nation" />
          <select data-testid="roll-collection-select" value={f.roll} onChange={set("roll")} className={inputCls}>
            <option value="">Any roll / collection</option>
            {allRolls.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {catalog.sources?.length ? (
            <select data-testid="roll-source-select" value={f.source_key} onChange={set("source_key")} className={inputCls}>
              <option value="">Any source document</option>
              {catalog.sources.map((s) => <option key={s.source_key} value={s.source_key}>Only: {s.roll_name}</option>)}
            </select>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-sm">
          {[["spelling_variants", "Include spelling variations"], ["phonetic", "Include phonetic matches"], ["search_relatives", "Search relatives & household"]].map(([k, label]) => (
            <label key={k} className="inline-flex items-center gap-2 cursor-pointer text-[#3F1E68]" data-testid={`toggle-${k}`}>
              <input type="checkbox" checked={f[k]} onChange={toggle(k)} className="accent-[#59288E]" /> {label}
            </label>
          ))}
        </div>
        <button data-testid="roll-search-btn" onClick={runSearch} disabled={busy}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-6 py-3 text-sm font-bold hover:bg-[#E7C965] transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search Rolls
        </button>
        <p className="mt-2 text-xs text-[#756D7E] flex items-center gap-1.5"><Globe size={12} className="text-[#59288E]" /> Searching also runs live AI Deep Research across public archives (Dawes index, NARA, Oklahoma Historical Society, FamilySearch, AccessGenealogy).</p>
      </Panel>

      {(deepBusy || deep) && (
        <div data-testid="deep-research-single">
          {deepBusy && !deep ? (
            <Panel className="p-5 mb-4 border-[#59288E]/25 bg-[#FCFAFF]">
              <div className="flex items-center gap-2 text-[#59288E] text-sm font-semibold"><Loader2 size={16} className="animate-spin" /> Running live AI Deep Research across public archives…</div>
              <p className="text-xs text-[#756D7E] mt-1">Querying real genealogy sources and analyzing our indexed rolls. This takes 15–40 seconds.</p>
            </Panel>
          ) : null}
          {deep ? <DeepResearchPanel report={deep.report} sources={deep.sources} dbMatchCount={deep.db_match_count} disclaimer={deep.disclaimer} title="AI Deep Research" subject={{ first: f.first, last: f.last, tribe: f.tribe, state: f.state, birth_year: f.birth_year }} onSave={deep.report ? saveResearch : null} /> : null}
        </div>
      )}

      {famMatches && (
        <div className="mb-6" data-testid="family-scan-results">
          <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-3 flex items-center gap-2"><Users size={18} className="text-[#59288E]" /> Matches across your family ({famMatches.length})</h3>
          <div className="space-y-3">
            {famMatches.map((m, i) => (
              <div key={i} className="rounded-xl border border-[#E4DCD3] bg-white p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STRENGTH_STYLE[m.strength]}`}>{m.strength} match</span>
                  <span className="text-sm font-semibold text-[#180D24]">{m.ancestor_name}</span>
                </div>
                <RecordCard rec={m.record} />
              </div>
            ))}
          </div>
        </div>
      )}

      {(famDeepBusy || famDeep) && (
        <div className="mb-6" data-testid="family-deep-research">
          <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-3 flex items-center gap-2"><Globe size={18} className="text-[#59288E]" /> AI Deep Research — your family</h3>
          {famDeepBusy ? (
            <Panel className="p-5 mb-4 border-[#59288E]/25 bg-[#FCFAFF]">
              <div className="flex items-center gap-2 text-[#59288E] text-sm font-semibold"><Loader2 size={16} className="animate-spin" /> Researching each family member across public archives…{famDeep?.total ? ` (${famDeep.done}/${famDeep.total})` : ""}</div>
              <p className="text-xs text-[#756D7E] mt-1">Running live web research + record analysis for your ancestors. Reports appear below as each one finishes — this can take a couple of minutes for a full family.</p>
            </Panel>
          ) : null}
          {famDeep?.reports?.map((r) => (
            <DeepResearchPanel key={r.ancestor_id} report={r.report} sources={r.sources} dbMatchCount={r.db_match_count} disclaimer={famDeep.disclaimer} title={`AI Deep Research — ${r.ancestor_name}`} subject={{ name: r.ancestor_name }} onSave={r.report ? saveResearch : null} />
          ))}
        </div>
      )}

      {results && (
        <div className="mb-6" data-testid="roll-search-results">
          <div className="rounded-xl bg-[#FFFBEB] border border-[#F59E0B]/30 p-3 text-xs text-[#B45309] mb-3 flex items-start gap-2"><ShieldAlert size={15} className="shrink-0 mt-0.5" />{disclaimer}</div>
          <h3 className="font-serif text-lg font-semibold text-[#180D24] mb-3">{results.length} record{results.length === 1 ? "" : "s"} found</h3>
          {results.length === 0 ? <Panel className="p-8 text-center text-[#756D7E] text-sm">No matching records. Try enabling spelling variations, phonetic matches, or a wildcard (e.g. John*).</Panel>
            : <div className="space-y-3">{results.map((r) => <RecordCard key={r.record_id} rec={r} />)}</div>}
        </div>
      )}

      {savedList.length ? (
        <div className="mb-6" data-testid="saved-research-vault">
          <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-1 flex items-center gap-2"><FolderOpen size={18} className="text-[#59288E]" /> Saved research ({savedList.length})</h3>
          <p className="text-sm text-[#756D7E] mb-3">Your saved Deep Research reports — reopen them any time without re-running the AI.</p>
          <div className="space-y-2">
            {savedList.map((it) => (
              <div key={it.id} data-testid={`saved-research-${it.id}`} className="rounded-xl border border-[#E4DCD3] bg-white p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-[#180D24] text-sm truncate">{it.title}</div>
                  <div className="text-xs text-[#8F7220] font-mono">{new Date(it.created_at).toLocaleDateString()} · {it.sources?.length || 0} sources · {it.db_match_count} record matches</div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`reopen-research-${it.id}`} onClick={() => reopenSaved(it)} className="inline-flex items-center gap-1.5 rounded-full bg-[#2A1442] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#482276] transition-colors"><FolderOpen size={13} /> Open</button>
                  <button onClick={() => exportResearchPdf({ title: it.title, report: it.report, sources: it.sources })} className="inline-flex items-center gap-1.5 rounded-full border border-[#59288E]/40 text-[#59288E] px-3 py-1.5 text-xs font-semibold hover:bg-[#59288E] hover:text-white transition-colors"><Download size={13} /> PDF</button>
                  <button data-testid={`delete-research-${it.id}`} onClick={() => deleteSaved(it.id)} className="inline-flex items-center rounded-full border border-[#E4DCD3] text-[#B91C1C] p-1.5 hover:bg-[#FEE2E2] transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {catalog.sources?.length ? (
        <div className="mb-6" data-testid="primary-sources">
          <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-1 flex items-center gap-2"><BookOpen size={18} className="text-[#59288E]" /> Primary source documents</h3>
          <p className="text-sm text-[#756D7E] mb-3">Original roll & census documents indexed into this search — open the source scans directly.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {catalog.sources.map((s) => (
              <a key={s.source_key} href={s.url} target="_blank" rel="noreferrer" data-testid={`source-doc-${s.source_key}`}
                className="rounded-xl border border-[#E4DCD3] bg-white p-4 hover:border-[#59288E] transition-colors block">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0">{s.kind === "image" ? <Image size={18} className="text-[#8F7220]" /> : <FileText size={18} className="text-[#59288E]" />}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#180D24] text-sm leading-snug">{s.title}</div>
                    <div className="text-xs text-[#8F7220] font-mono mt-0.5">{[s.tribe, s.year, `${s.pages} pages`, s.archive].filter(Boolean).join(" · ")}</div>
                    <div className="text-xs text-[#756D7E] mt-1">{s.desc}</div>
                    {s.kind === "image" ? (
                      <div className="mt-2 text-[11px] font-semibold" data-testid={`ocr-status-${s.source_key}`}>
                        {!ocrMap[s.source_key]
                          ? <span className="text-[#756D7E] inline-flex items-center gap-1"><Loader2 size={12} /> Queued for AI OCR</span>
                          : ocrMap[s.source_key].status === "done"
                            ? <span className="text-[#166534] inline-flex items-center gap-1"><Check size={12} /> OCR complete · {ocrMap[s.source_key].records} names searchable</span>
                            : <span className="text-[#B45309] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> AI OCR in progress · {(ocrMap[s.source_key].pages_done || []).length}/{ocrMap[s.source_key].total_pages} pages · {ocrMap[s.source_key].records || 0} names</span>}
                      </div>
                    ) : null}
                    <div className="text-xs text-[#59288E] font-semibold mt-2 inline-flex items-center gap-1"><ExternalLink size={12} /> Open source document</div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      ) : null}

      <h3 className="font-serif text-xl font-semibold text-[#180D24] mb-3 flex items-center gap-2"><ScrollText size={18} className="text-[#D4AF37]" /> Browse the rolls</h3>
      <div className="space-y-2">
        {catalog.catalog.map((era) => (
          <Panel key={era.era} className="overflow-hidden">
            <button data-testid={`era-${era.era.slice(0, 4)}`} onClick={() => setOpenEra(openEra === era.era ? null : era.era)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-[#FAF8F5] transition-colors">
              <span className="font-serif font-semibold text-[#180D24]">{era.era}</span>
              {openEra === era.era ? <ChevronDown size={18} className="text-[#59288E]" /> : <ChevronRight size={18} className="text-[#9CA3AF]" />}
            </button>
            {openEra === era.era && (
              <div className="px-4 pb-4 space-y-2">
                {era.rolls.map((r) => (
                  <div key={r.name} className="rounded-xl border border-[#E4DCD3] p-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-semibold text-[#180D24] text-sm">{r.name}</div>
                        <div className="text-xs text-[#8F7220] font-mono">{r.tribe} · {r.years}</div>
                      </div>
                      <button data-testid={`search-roll-${r.name.slice(0, 8)}`}
                        onClick={() => { setF({ ...BLANK, roll: r.name, tribe: "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="text-xs font-semibold text-[#59288E] hover:text-[#180D24] inline-flex items-center gap-1"><Search size={12} /> Search this roll</button>
                    </div>
                    <div className="text-xs text-[#756D7E] mt-1">{r.desc}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        ))}
      </div>

      {catalog.is_admin && (
        <Panel className="p-5 mt-6" data-testid="roll-import-panel">
          <div className="flex items-center gap-2 mb-2 text-[#180D24] font-serif text-lg font-semibold"><Database size={18} className="text-[#59288E]" /> Import Roll Data (admin)</div>
          <p className="text-sm text-[#756D7E] mb-1">Currently indexed: <span className="font-semibold text-[#180D24]">{catalog.record_count}</span> records{catalog.imported_count ? ` (${catalog.imported_count} imported)` : ""}. Upload lawfully-obtained roll data as CSV to expand the searchable index at any scale.</p>
          <p className="text-[11px] text-[#8F7220] font-mono mb-3">Columns: {catalog.csv_columns?.join(", ")} · family_members separated by “;”.</p>
          <input ref={importRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="roll-import-input"
            onChange={(e) => { if (e.target.files?.[0]) importCsv(e.target.files[0]); e.target.value = ""; }} />
          <div className="flex flex-wrap gap-2">
            <button data-testid="roll-import-btn" onClick={() => importRef.current?.click()} disabled={importing}
              className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60">
              {importing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} className="text-[#E7C965]" />} Upload CSV
            </button>
            <button data-testid="roll-template-btn" onClick={downloadTemplate}
              className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] px-5 py-2.5 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
              <FileDown size={15} /> Download CSV template
            </button>
          </div>
        </Panel>
      )}
    </div>
  );
}
